import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of, take } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { CommonService } from '../api/services/utility-services/common.service';
import { SessionDto } from '../models/session/sessiondto';
import { RoleConstants } from '../constants/role.constants';
import { PartnersUsersService } from '../api/services/partners-accounts/users/partners-users.service';
import { UsersSearchDto } from '../models/partnerAccounts/users/users-searchDto';
import { UrlConstants } from '../constants/url.constants';
import { GenericStateService } from '../api/services/utility-services/generic-state.service';
import { LoginResponse } from '../models/login/login-response';
import { LoginService } from '../api/services/login/login.service';
import { SessionCacheUtil } from '../util/session-cache.util';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly commonService = inject(CommonService);
  generateService =inject(GenericStateService)
  userService=inject(PartnersUsersService)
  loginService=inject(LoginService)
  private readonly router = inject(Router);

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.commonService.isAuthenticated();
  }

  /**
   * Get current user session
   */
  getCurrentUser(): SessionDto | null {
    return this.commonService.getCurrentUser();
  }

  /**
   * Check if user has specific role
   */
  hasRole(role: string): boolean {
    const user = this.getCurrentUser();
    return user ? user.userRoles?.includes(role) || false : false;
  }

  /**
   * Check if user has any of the specified roles
   */
  hasAnyRole(roles: string[]): boolean {
    const user = this.getCurrentUser();
    if (!user || !user.userRoles) return false;
    return roles.some(role => user.userRoles.includes(role));
  }

  /**
   * Check if user has all of the specified roles
   */
  hasAllRoles(roles: string[]): boolean {
    const user = this.getCurrentUser();
    if (!user || !user.userRoles) return false;
    return roles.every(role => user.userRoles.includes(role));
  }

  /**
   * Check if user is customer
   */
  isCustomer(): boolean {
    return this.hasRole(RoleConstants.CUSTOMER) || this.hasRole(RoleConstants.CUSTOMER_SUPER_USER);
  }

  /**
   * Check if user is partner
   */
  isPartner(): boolean {
    return this.hasRole(RoleConstants.PARTNER) ||
           this.hasRole(RoleConstants.SHARED_PARTNER) ||
           this.hasRole(RoleConstants.PARTNER_ADMIN) ||
           this.hasRole(RoleConstants.PARTNER_MGR);
  }

  isCustomerSuperUser(): boolean{
    return this.hasRole(RoleConstants.CUSTOMER_SUPER_USER);
  }

  /**
   * Logout user
   */
  logout(): Observable<LoginResponse> {
    // Call logout service
    return this.loginService.logout().pipe(
      tap(res =>{
        if(res.redirectUrl === UrlConstants.homeUrl && (res.redirectUrl === UrlConstants.accountSearchPartnerUrl)){
          this.commonService.session().subscribe();
        }
        else{
          this.generateService.clearData();
          SessionCacheUtil?.clearSessionStorage();
          this.userService.setUserData(new UsersSearchDto());
          this.commonService.clearSession();
        }
      }),
      take(1)
    );
  }

  // url comes exclusively from saml_idp_registration.post_logout_redirect_url (DB/staff-controlled).
  // startsWith('http') distinguishes external customer portals from CRC-internal paths — not a trust check.
  public redirectAfterLogout(url: string | undefined): void {
    if (url && url.startsWith('http')) {
      this.navigateExternal(url);
    } else {
      this.router.navigate(['/login']);
    }
  }

  // Extracted to allow spying in tests (window.location cannot be mocked in Chrome/Karma directly).
  navigateExternal(url: string): void {
    window.location.href = url;
  }
}
